import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Briefcase, TrendingUp, DollarSign, Trash2, Download } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useDivision } from '@/context/DivisionContext';
import { getDistrictByCounty, getAllDistricts } from '@/utils/txdotDistricts';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Projects = () => {
  const { division } = useDivision();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [districtFilter, setDistrictFilter] = useState('all');
  const [sortBy, setSortBy] = useState('project_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const navigate = useNavigate();
  const { getAuthHeaders, isAdmin } = useAuth();

  useEffect(() => {
    fetchProjects();
  }, [division]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, {
        headers: getAuthHeaders(),
        params: { division }
      });
      
      // For completed projects, fetch daily data to calculate actual costs
      const projectsWithProfit = await Promise.all(
        response.data.map(async (project) => {
          if (project.status === 'completed') {
            try {
              const dailyDataResponse = await axios.get(
                `${API}/projects/${project.id}/daily-data`,
                { headers: getAuthHeaders() }
              );
              
              const totalCosts = dailyDataResponse.data.reduce((sum, day) => 
                sum + (parseFloat(day.payroll) || 0) + 
                      (parseFloat(day.fuel) || 0) + 
                      (parseFloat(day.hotel) || 0) + 
                      (parseFloat(day.food) || 0) + 
                      (parseFloat(day.maintenance) || 0) + 
                      (parseFloat(day.rental) || 0), 0
              );
              
              const profit = project.bid_amount - totalCosts;
              return { ...project, actual_costs: totalCosts, profit };
            } catch (err) {
              return project;
            }
          }
          return project;
        })
      );
      
      setProjects(projectsWithProfit);
      setFilteredProjects(projectsWithProfit);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
      setLoading(false);
    }
  };

  // Filter and sort projects
  useEffect(() => {
    let filtered = [...projects];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(project => {
        const name = project.project_name || '';
        const county = project.county || '';
        const contractor = project.contractor_name || '';
        const searchLower = searchTerm.toLowerCase();
        
        return name.toLowerCase().includes(searchLower) ||
               county.toLowerCase().includes(searchLower) ||
               contractor.toLowerCase().includes(searchLower);
      });
    }

    // Apply district filter (Texas only)
    if (division === 'texas' && districtFilter !== 'all') {
      filtered = filtered.filter(project => {
        const district = getDistrictByCounty(project.county);
        return district === districtFilter;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'project_name':
          aVal = a.project_name || '';
          bVal = b.project_name || '';
          break;
        case 'contractor':
          aVal = a.contractor_name || '';
          bVal = b.contractor_name || '';
          break;
        case 'amount':
          aVal = a.bid_amount || 0;
          bVal = b.bid_amount || 0;
          break;
        case 'completion':
          aVal = a.completion_percentage || 0;
          bVal = b.completion_percentage || 0;
          break;
        default:
          aVal = a.project_name || '';
          bVal = b.project_name || '';
      }

      if (typeof aVal === 'string') {
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      } else {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
    });

    setFilteredProjects(filtered);
  }, [projects, searchTerm, districtFilter, sortBy, sortOrder, division]);

  // Excel export function
  const exportToExcel = () => {
    const csvData = filteredProjects.map(project => ({
      'Project Name': project.project_name || '',
      'County': project.county || '',
      'Highway': project.highway || '',
      'Control Number': project.control_number || '',
      'Contractor': project.contractor_name || '',
      'Bid Amount': project.bid_amount || 0,
      'Total LF': project.total_lf || 0,
      'Completion %': project.completion_percentage || 0,
      'Days On Site': project.days_on_site || 0,
      'District': division === 'texas' ? getDistrictByCounty(project.county) : 'N/A'
    }));

    const csv = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `active-projects-${division}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Exported to CSV successfully!');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const promptDeleteProject = (project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteProject = async () => {
    if (!projectToDelete) return;

    try {
      await axios.delete(`${API}/projects/${projectToDelete.id}`, {
        headers: getAuthHeaders()
      });
      toast.success(`Project "${projectToDelete.project_name || projectToDelete.control_number}" deleted successfully`);
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
      fetchProjects(); // Refresh the list
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-[#10b981] text-white';
      case 'completed':
        return 'bg-[#6b7280] text-white';
      default:
        return 'bg-[#0ea5e9] text-white';
    }
  };

  return (
    <div className="relative min-h-screen">
      <div className="noise-overlay"></div>
      
      <div className="relative z-10 ml-64 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-2">
                <span className="text-[#f97316]">Active</span> Projects
              </h1>
              <p className="text-[#94a3b8] text-sm uppercase tracking-widest">
                {filteredProjects.length} Projects • ${filteredProjects.reduce((sum, p) => sum + (p.bid_amount || 0), 0).toLocaleString()} Total Value
              </p>
            </div>
            <Button
              onClick={exportToExcel}
              className="millmetrix-button bg-[#10b981] hover:bg-[#059669] text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Export to Excel
            </Button>
          </div>

          {/* Search and Filters */}
          <Card className="millmetrix-card p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                  Search
                </Label>
                <Input
                  type="text"
                  placeholder="Search by project name, county, or contractor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="millmetrix-input"
                />
              </div>
              
              {division === 'texas' && (
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                    TXDOT District
                  </Label>
                  <select
                    value={districtFilter}
                    onChange={(e) => setDistrictFilter(e.target.value)}
                    className="w-full bg-[#1e293b] text-white px-4 py-2.5 rounded-lg border border-[rgba(255,255,255,0.1)] hover:border-[#f97316] focus:border-[#f97316] focus:outline-none transition-all"
                  >
                    <option value="all">All Districts</option>
                    {getAllDistricts().map(district => (
                      <option key={district} value={district}>{district}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                  Sort By
                </Label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full bg-[#1e293b] text-white px-4 py-2.5 rounded-lg border border-[rgba(255,255,255,0.1)] hover:border-[#f97316] focus:border-[#f97316] focus:outline-none transition-all"
                >
                  <option value="project_name">Project Name</option>
                  <option value="contractor">Contractor</option>
                  <option value="amount">Bid Amount</option>
                  <option value="completion">Completion %</option>
                </select>
              </div>
            </div>
            
            <div className="mt-4 flex items-center gap-2">
              <Button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                variant="outline"
                size="sm"
                className="border-[#f97316] text-[#f97316] hover:bg-[#f97316] hover:text-white"
              >
                {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
              </Button>
              {(searchTerm || districtFilter !== 'all') && (
                <Button
                  onClick={() => {
                    setSearchTerm('');
                    setDistrictFilter('all');
                  }}
                  variant="outline"
                  size="sm"
                  className="border-[#94a3b8] text-[#94a3b8] hover:bg-[#94a3b8] hover:text-white"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </Card>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card className="millmetrix-card p-6">
              <div className="flex items-center gap-3 mb-2">
                <Briefcase className="w-5 h-5 text-[#f97316]" />
                <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Active Projects
                </div>
              </div>
              <div className="text-4xl font-mono font-bold tracking-tight text-[#f8fafc] tabular-nums">
                {projects.filter(p => p.status === 'active').length}
              </div>
            </Card>

            <Card className="millmetrix-card p-6">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-5 h-5 text-[#10b981]" />
                <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Total Contract Value
                </div>
              </div>
              <div className="text-4xl font-mono font-bold tracking-tight text-[#10b981] tabular-nums">
                {formatCurrency(projects.reduce((sum, p) => sum + (p.bid_amount || 0), 0))}
              </div>
            </Card>

            <Card className="millmetrix-card p-6">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-[#0ea5e9]" />
                <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Avg Completion
                </div>
              </div>
              <div className="text-4xl font-mono font-bold tracking-tight text-[#0ea5e9] tabular-nums">
                {projects.length > 0 
                  ? Math.round(projects.reduce((sum, p) => sum + (p.completion_percentage || 0), 0) / projects.length)
                  : 0}%
              </div>
            </Card>
          </div>

          {/* Projects List */}
          <Card className="millmetrix-card p-6">
            <h2 className="text-2xl font-bold tracking-tight mb-6 text-[#f8fafc]">All Projects</h2>
            
            {loading ? (
              <div className="text-center py-12 text-[#94a3b8]">Loading projects...</div>
            ) : filteredProjects.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-[#94a3b8] mb-4">
                  {projects.length === 0 
                    ? 'No active projects yet. Win bids in Open Bids to create projects!'
                    : 'No projects match your filters.'}
                </p>
                {projects.length === 0 && (
                  <Button
                    onClick={() => navigate('/open-bids')}
                    className="millmetrix-button bg-[#f97316] hover:bg-[#ea580c] text-white"
                  >
                    Go to Open Bids
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredProjects.map((project) => (
                  <div
                    key={project.id}
                    className="bg-[#1e293b] p-4 rounded-md border border-[rgba(255,255,255,0.05)] hover:border-[#f97316] transition-all"
                    data-testid={`project-${project.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-[#f8fafc]">
                            {project.project_name || `${project.county} - ${project.highway}`}
                            <span className="ml-2 font-mono text-[#f97316]">#{project.control_number}</span>
                          </h3>
                          <span className={`text-sm font-mono uppercase px-2 py-1 rounded ${getStatusColor(project.status)}`}>
                            {project.status}
                          </span>
                        </div>
                        
                        <div className="text-sm mb-3">
                          <span className="text-[#94a3b8]">Contractor: </span>
                          <span className="font-semibold text-[#f97316]">{project.contractor_name || 'Unknown'}</span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                          <div>
                            <span className="text-[#94a3b8]">Contract: </span>
                            <span className="font-mono text-[#f8fafc]">{formatCurrency(project.bid_amount)}</span>
                          </div>
                          <div>
                            <span className="text-[#94a3b8]">Total LF: </span>
                            <span className="font-mono text-[#f8fafc]">{project.total_lf?.toLocaleString() || 0}</span>
                          </div>
                          <div>
                            <span className="text-[#94a3b8]">Days: </span>
                            <span className="font-mono text-[#f8fafc]">{project.days_on_site || 0} / {project.estimated_days || 0}</span>
                          </div>
                          <div>
                            <span className="text-[#94a3b8]">Completion: </span>
                            <span className="font-mono text-[#10b981]">{project.completion_percentage || 0}%</span>
                          </div>
                        </div>
                        
                        {project.status === 'completed' && project.profit !== undefined && (
                          <div className="bg-[#0f172a] p-3 rounded-md border border-[#10b981] mb-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="text-xs text-[#94a3b8] uppercase tracking-widest">Final Profit</span>
                                <div className="text-2xl font-mono font-bold text-[#10b981]">
                                  {formatCurrency(project.profit)}
                                </div>
                              </div>
                              <div className="text-right">
                                <span className="text-xs text-[#94a3b8] uppercase tracking-widest">Actual Costs</span>
                                <div className="text-lg font-mono text-[#ef4444]">
                                  {formatCurrency(project.actual_costs)}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Progress Bar */}
                        <div className="w-full bg-[#0f172a] rounded-full h-2">
                          <div 
                            className="bg-[#10b981] h-2 rounded-full transition-all"
                            style={{ width: `${project.completion_percentage || 0}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 ml-4">
                        <Button
                          size="sm"
                          onClick={() => navigate(`/operations/${project.id}`)}
                          className="bg-[#f97316] hover:bg-[#ea580c] text-white"
                          data-testid={`view-operations-${project.id}`}
                        >
                          View Operations
                        </Button>
                        {isAdmin && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => promptDeleteProject(project)}
                            className="bg-[#ef4444] hover:bg-[#dc2626] text-white"
                            data-testid={`delete-project-${project.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#0f172a] border-[#1e293b]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#f8fafc]">Delete Project</AlertDialogTitle>
            <AlertDialogDescription className="text-[#94a3b8]">
              Are you sure you want to delete project{' '}
              <span className="font-semibold text-[#f97316]">
                {projectToDelete?.project_name || projectToDelete?.control_number}
              </span>
              ? This action cannot be undone and will also delete all associated daily operations data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#1e293b] border-[#334155] text-[#f8fafc] hover:bg-[#334155]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteProject}
              className="bg-[#ef4444] hover:bg-[#dc2626] text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Projects;
